/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import {Control, Controller, FieldErrors} from 'react-hook-form'
import clsx from 'clsx'
import {AntdDatePicker} from 'components/Ui/AntdPickerWrapper'
import dayjs, {Dayjs} from 'dayjs'
import {useTranslation} from 'react-i18next'
import {CalendarIcon} from '@heroicons/react/24/outline'
import {useDebouncedCallback} from 'utils/hooks/useDebounce'
import {DATE_FORMATS} from 'constants/DateFormats'
import {useUiTheme} from 'theme/UiThemeProvider'
import {uiFormControlFocusClassName, uiFormControlPlaceholderClassName, uiFormControlSurfaceClassName,} from './UiFormControlStyles'

import 'dayjs/locale/de'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/en'
import 'dayjs/locale/nl'
import 'dayjs/locale/sk'
import 'dayjs/locale/hu'
import 'dayjs/locale/it'

interface UiDatePickerProps {
    control?: Control<any>
    name?: string
    label?: string
    errors?: FieldErrors
    error?: string
    className?: string
    containerClassName?: string
    required?: boolean
    disabled?: boolean
    placeholder?: string
    minDate?: Date
    maxDate?: Date
    value?: Date | string | null
    onChange?: (date: Date | null) => void
    format?: string
    endIcon?: React.ReactNode
    includeTime?: boolean
    inputReadOnly?: boolean
}

const UiDatePicker = (props: UiDatePickerProps) => {
    const { t } = useTranslation()
    const { mode } = useUiTheme()
    const isDark = mode === 'dark'
    const errorMessage = props.error || props.errors?.[props.name || '']?.message;

    // Debounce the onChange callback to improve performance
    const debouncedOnChange = useDebouncedCallback(
        (nativeDate: Date | null) => props.onChange?.(nativeDate),
        300
    )

    const renderPicker = (field?: any) => {
        const value = field?.value ?? props.value
        const parsedDateValue = value ? dayjs(value) : null
        const dateValue = parsedDateValue && parsedDateValue.isValid() ? parsedDateValue : null

        const handleChange = (date: Dayjs | null) => {
            const nativeDate = date ? date.toDate() : null
            if (field) {
                field.onChange(nativeDate)
            }
            debouncedOnChange(nativeDate)
        }

        const disabledDate = (current: Dayjs) => {
            if (
                props.minDate &&
                current.isBefore(dayjs(props.minDate), 'day')
            ) {
                return true
            }
            if (props.maxDate && current.isAfter(dayjs(props.maxDate), 'day')) {
                return true
            }
            return false
        }

        const hasError = !!(
            props.error || props.errors?.[props.name || '']?.message
        )

        return (
            <div
                className={clsx(
                    'relative flex items-center overflow-hidden',
                    uiFormControlSurfaceClassName,
                    uiFormControlFocusClassName,
                    hasError
                        ? 'border-red-500'
                        : uiFormControlPlaceholderClassName,
                    props.disabled &&
                    'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900'
                )}
            >
                <AntdDatePicker
                    allowClear={!props.required}
                    className={clsx(
                        'w-full h-10 px-3 bg-transparent border-0 text-gray-900 dark:text-gray-100 focus:border-0 shadow-none'
                    )}
                    style={{ borderWidth: 0, width: '100%' }}
                    format={
                        props.format ||
                        (props.includeTime
                            ? DATE_FORMATS.DATE_TIME
                            : DATE_FORMATS.DATE)
                    }
                    showTime={
                        props.includeTime
                            ? { format: 'HH:mm', minuteStep: 10, hideDisabledOptions: true }
                            : false
                    }
                    needConfirm={false}
                    disabledTime={props.includeTime ? () => ({ disabledHours: () => [0, 1, 2, 3, 4, 5] }) : undefined}
                    suffixIcon={props.endIcon || <CalendarIcon className="h-5 w-5 text-gray-800 dark:text-gray-200" />}
                    value={dateValue}
                    onChange={handleChange}
                    disabled={props.disabled}
                    disabledDate={disabledDate}
                    placeholder={
                        props.placeholder ||
                        (props.includeTime
                            ? t('Select date and time')
                            : t('Select date'))
                    }
                    inputReadOnly={props.inputReadOnly ?? true}
                />
            </div>
        )
    }

    return (
        <div
            className={clsx(
                'UiDatePicker flex flex-col gap-1.5',
                props.className,
                props.containerClassName
            )}
        >
            {props.label && (
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {props.label}
                    {props.required && (
                        <span className="text-red-500 ml-0.5">*</span>
                    )}
                </label>
            )}

            <div className="relative w-full">
                {props.control && props.name ? (
                    <Controller
                        control={props.control}
                        name={props.name}
                        render={({ field }) => renderPicker(field)}
                    />
                ) : (
                    renderPicker()
                )}
            </div>

            {errorMessage && (
                <span className="text-red-500 text-xs mt-1">
                    {String(errorMessage)}
                </span>
            )}
        </div>
    )
}

export default UiDatePicker
